import { motion } from "framer-motion";
import { ArrowRight, MapPin, Battery, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { CHARGER_IMAGES } from "../constants/chargerImages";

// MILE PLUS Hero Logo Component
const MilePlusHeroLogo = () => (
  <div className="relative inline-block">
    <h1 
      className="font-heading font-black text-7xl md:text-8xl lg:text-9xl tracking-[0.15em] uppercase"
      style={{
        background: 'linear-gradient(135deg, #CCFF00 0%, #E8FF66 30%, #CCFF00 60%, #B3E600 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        filter: 'drop-shadow(0 0 60px rgba(204, 255, 0, 0.4))',
      }}
    >
      MILE PLUS
    </h1>
    {/* Animated underline */}
    <motion.div 
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ delay: 0.5, duration: 0.8 }}
      className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent mt-2"
    />
    {/* Lightning bolt accent */}
    <motion.svg 
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.8 }}
      className="absolute -top-4 -right-8 w-10 h-10 text-primary" 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
    </motion.svg>
  </div>
);

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={CHARGER_IMAGES.hero.main}
          alt="MILE PLUS EV Charging Station"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(204,255,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(204,255,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text */}
          <div className="space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10"
            >
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">
                Based in Nashik, Maharashtra
              </span>
            </motion.div>

            {/* Main Logo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <MilePlusHeroLogo />
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-2"
            >
              <p className="font-heading text-2xl md:text-3xl text-foreground tracking-wide">
                POWERING MAHARASHTRA'S
              </p>
              <p className="font-heading text-2xl md:text-3xl text-secondary tracking-wide">
                EV REVOLUTION
              </p>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-foreground-muted max-w-lg"
            >
              Experience the most reliable EV charging network in Maharashtra.
              Fast charging with premium Cubenz chargers, seamless payments, and 24/7 
              availability across Nashik and beyond.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <Button
                onClick={() => scrollToSection("#stations")}
                className="rounded-full bg-primary text-black font-bold hover:shadow-[0_0_30px_rgba(204,255,0,0.5)] transition-all px-8 py-6 text-lg group"
                data-testid="hero-find-stations-btn"
              >
                Find Stations
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection("#franchise")}
                variant="outline"
                className="rounded-full border-primary/50 text-primary hover:bg-primary/10 px-8 py-6 text-lg"
                data-testid="hero-franchise-btn"
              >
                Become a Partner
              </Button>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-8 pt-8 border-t border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/30">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-heading text-primary">50+</p>
                  <p className="text-sm text-foreground-muted">Stations</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center border border-secondary/30">
                  <Battery className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-heading text-secondary">24/7</p>
                  <p className="text-sm text-foreground-muted">Available</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center border border-accent/30">
                  <MapPin className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-heading text-accent">15+</p>
                  <p className="text-sm text-foreground-muted">Cities</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Charger Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Main Charger Image */}
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={CHARGER_IMAGES.dc.zeus120kW}
                  alt="MILE PLUS 120kW DC Fast Charger"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                
                {/* Charger Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="glass rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-foreground-muted text-sm">Featured Charger</p>
                        <p className="font-heading text-2xl text-primary">Zeus 120kW</p>
                        <p className="text-foreground-muted text-sm">DC Ultra-Fast</p>
                      </div>
                      <div className="text-right">
                        <p className="text-foreground-muted text-xs">Charging Time</p>
                        <p className="font-heading text-xl text-secondary">25-35 min</p>
                        <p className="text-foreground-muted text-xs">to 80%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Card - Power */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 glass rounded-2xl p-4 border border-primary/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <Zap className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-foreground-muted text-xs">Max Power</p>
                    <p className="font-heading text-xl text-primary">240 kW</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card - Cost */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 border border-secondary/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                    <Battery className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-foreground-muted text-xs">Cost per km</p>
                    <p className="font-heading text-xl text-secondary">Rs 1.5</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 rounded-full bg-primary" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
